// Product workspace card: category tabs + product grid + purchase row.
// Mirrors section.card.product-workspace in the approved HTML and ports
// renderProducts() plus the mode toggles to React state.
//
// W36 (2026-10-20): the card no longer reads a price off the catalog
// entry. Prices arrive through `prices` (keyed by engine handle): the
// design preview passes the example table, a real session passes the
// pricing engine's catalog quote, and a missing entry renders an honest
// "Price pending" tile. The barn strip on a real session shows the tier
// the engine resolved instead of the design's "$X" placeholder.
import type { ReactNode } from 'react';
import { Icon } from '../../_shared/IconSprite.js';
import { formatCents } from '../../../data/livePricing.js';
import {
  type CategoryKey,
  type CatalogEntry,
  productTile,
} from '../../../data/productCatalog.js';

export interface TilePrice {
  /** Unit price in cents at the current purchase type; null when not known. */
  unitCents: number | null;
  /** Small caption after the size ("Example", or the Shopify SKU on a real session). */
  caption: string;
}

export type TilePrices = Record<string, TilePrice | undefined>;

export interface TierStrip {
  title: string;
  body: string;
  tag: string;
}

export interface ProductWorkspaceProps {
  category: CategoryKey;
  catalog: CatalogEntry[];
  quantities: Record<string, number>;
  subscription: boolean;
  frequencyDays: string;
  /** Unit prices keyed by engine handle. */
  prices: TilePrices;
  /** Shown on a tile with no price (real session while the engine is pending). */
  pendingPriceLabel?: string;
  /** Real-session tier strip; undefined keeps the design-preview strip. */
  tierStrip?: TierStrip;
  /** 'real' drops the design-preview "Example" captions. */
  mode?: 'preview' | 'real';
  onPickCategory: (c: CategoryKey) => void;
  onAdjustQty: (name: string, delta: number) => void;
  onSetSubscription: (sub: boolean) => void;
  onSetFrequency: (days: string) => void;
  onReviewEquipment: (which: 'massager' | 'laser') => void;
  /** W32: real first name on a real session. Defaults to the design-preview customer. */
  customerFirstName?: string;
  /** W32: real session price caption. Defaults to the design-preview caption. */
  priceCaption?: string;
}

const CATS: CategoryKey[] = ['Equine', 'Human', 'Canine', 'Equipment'];

export function ProductWorkspace(props: ProductWorkspaceProps): ReactNode {
  const {
    category,
    catalog,
    quantities,
    subscription,
    frequencyDays,
    prices,
    pendingPriceLabel = 'Price pending',
    tierStrip,
    mode = 'preview',
    onPickCategory,
    onAdjustQty,
    onSetSubscription,
    onSetFrequency,
    onReviewEquipment,
    customerFirstName = 'Sarah',
    priceCaption = 'Example prices · No live order',
  } = props;

  const basketCount = catalog.reduce((a, p) => a + (quantities[p.name] ?? 0), 0);

  return (
    <section className="card product-workspace">
      <div className="card-head">
        <div className="row">
          <span className="head-icon">
            <Icon id="box" />
          </span>
          <h3>Choose {customerFirstName}'s products</h3>
        </div>
        <span className="small">{priceCaption}</span>
      </div>
      <div className="catalog-tools">
        <div className="category-tabs" role="group" aria-label="Product category">
          {CATS.map((c) => (
            <button
              key={c}
              type="button"
              data-cat={c}
              aria-pressed={category === c}
              onClick={() => onPickCategory(c)}
            >
              {c}
            </button>
          ))}
        </div>
        <span id="basket-count">{basketCount} items in basket</span>
      </div>
      <div id="products" className="product-grid">
        {category === 'Human' || category === 'Canine' ? (
          <div className="catalog-empty">
            <strong>{category} products</strong>
            <p>Product names, sizes, prices and stock will come from your catalog.</p>
            <p>Awaiting actual catalog details. No products invented.</p>
          </div>
        ) : category === 'Equipment' ? (
          <>
            <article className="product-tile">
              <div className="product-top">
                <div
                  className="product-art"
                  style={{ ['--tint' as any]: '#e3edff', ['--color' as any]: '#5b83b4' }}
                >
                  <Icon id="box" />
                </div>
                <div>
                  <h4>EquiMassager</h4>
                  <span className="stock">In stock · Example</span>
                </div>
              </div>
              <p>One-time equipment item</p>
              <button
                className="equipment-button"
                type="button"
                onClick={() => onReviewEquipment('massager')}
              >
                Review availability &amp; price
              </button>
            </article>
            <article className="product-tile">
              <div className="product-top">
                <div
                  className="product-art"
                  style={{ ['--tint' as any]: '#f1e5f9', ['--color' as any]: '#8563a1' }}
                >
                  <Icon id="box" />
                </div>
                <div>
                  <h4>EquiLaser</h4>
                  <span className="stock wait">Out of stock · Example</span>
                </div>
              </div>
              <p>Price and terms at quote</p>
              <button
                className="equipment-button"
                type="button"
                onClick={() => onReviewEquipment('laser')}
              >
                Order now and wait
              </button>
            </article>
          </>
        ) : (
          catalog.map((p) => {
            const qty = quantities[p.name] ?? 0;
            const price = prices[p.handle];
            return (
              <article
                key={p.handle}
                className={`product-tile has-product-image${qty ? ' chosen' : ''}`}
                data-testid={`product-tile-${p.handle}`}
              >
                <div className="product-top">
                  <div
                    className="product-art"
                    style={{ ['--tint' as any]: p.tint, ['--color' as any]: p.color }}
                  >
                    <img src={productTile(p)} alt={`${p.name} product`} />
                  </div>
                  <div>
                    <h4>{p.name}</h4>
                    <p>{p.detail}</p>
                  </div>
                </div>
                <div className="product-bottom">
                  <span className="unit-price" data-testid={`unit-price-${p.handle}`}>
                    {price && price.unitCents !== null ? formatCents(price.unitCents) : pendingPriceLabel}
                    <small>{p.size}{price ? ` · ${price.caption}` : ''}</small>
                  </span>
                  <div className="qty">
                    <button
                      aria-label={`Decrease ${p.name}`}
                      type="button"
                      onClick={() => onAdjustQty(p.name, -1)}
                    >
                      {'−'}
                    </button>
                    <span aria-label={`${p.name} quantity`}>{qty}</span>
                    <button
                      aria-label={`Increase ${p.name}`}
                      type="button"
                      onClick={() => onAdjustQty(p.name, 1)}
                    >
                      +
                    </button>
                  </div>
                </div>
              </article>
            );
          })
        )}
      </div>
      <div className="purchase-row">
        <div>
          <span className="eyebrow">PURCHASE TYPE</span>
          <div className="purchase-switch">
            <button
              id="once"
              type="button"
              aria-pressed={!subscription}
              onClick={() => onSetSubscription(false)}
            >
              One-time
            </button>
            <button
              id="subscription"
              type="button"
              aria-pressed={subscription}
              onClick={() => onSetSubscription(true)}
            >
              <Icon id="refresh" />
              Subscription
            </button>
          </div>
        </div>
        <div id="frequency-wrap" hidden={!subscription}>
          <label htmlFor="frequency">DELIVERY FREQUENCY</label>
          <select
            id="frequency"
            value={frequencyDays}
            onChange={(e) => onSetFrequency(e.target.value)}
          >
            <option value="30">Every 30 days</option>
            <option value="60">Every 60 days</option>
            <option value="90">Every 90 days</option>
          </select>
          <small>{mode === 'real'
            ? 'Recharge supplies the available intervals once the draft order is connected.'
            : 'Example intervals. Recharge supplies available options.'}</small>
        </div>
      </div>
      {tierStrip ? (
        <div className="barn-strip" data-testid="order-tier-strip">
          <span className="barn-icon">
            <Icon id="people" />
          </span>
          <div>
            <strong>{tierStrip.title}</strong>
            <p>{tierStrip.body}</p>
          </div>
          <span className="barn-tag">{tierStrip.tag}</span>
        </div>
      ) : (
        <div className="barn-strip">
          <span className="barn-icon">
            <Icon id="people" />
          </span>
          <div>
            <strong>Add $X more to qualify for Barn Pricing</strong>
            <p>Your best eligible price, supplied by the pricing engine.</p>
          </div>
          <span className="barn-tag">Barn pricing</span>
        </div>
      )}
    </section>
  );
}
